import React, { useEffect } from 'react';
import { Box, Stack, Typography, Divider } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import EventsTable from '../components/EventsTable';
import EventsSkeleton from '../components/EventsSkeleton';
import EventsEmptyState from '../components/EventsEmptyState';
import EventDetailPane from '../components/EventDetailPane';
import LoadMoreButton from '../../../components/LoadMoreButton';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useGetEventsHandler } from '../dataHandlers/getEventsHandler';
import { PaginationDefaults } from '../../../constants/defaults';
import useEventFilters from '../../../hooks/useEventFilters';
import EventFilters from '../components/EventFilters';

const EventsListPage = () => {
  useDocumentTitle('Events');
  const { eventId } = useParams();
  const navigate = useNavigate();

  const {
    selectedFilter,
    searchTerm,
    selectedStatus,
    currentQuery,
    handleFilterChange,
    handleSearchChange,
    handleSearchKeyPress,
    handleStatusChange,
    appliedSearchTerm,
  } = useEventFilters({ defaultQuery: {}, searchOnChange: true });

  const { events, hasMore, isLoading, isLoadingMore, loadMore } = useGetEventsHandler({ currentQuery });

  // Auto-select first event when landing on events page
  useEffect(() => {
    if (isLoading || events.length === 0) return;

    const existsInList = eventId && events.some((e) => e.id === eventId);

    if (!existsInList) {
      navigate(`/events/${events[0].id}`, { replace: true });
    }
  }, [eventId, events, isLoading, navigate]);

  // Render loading state
  if (isLoading) {
    return (
      <Box sx={{ py: 1.5, px: 3 }}>
        <EventFilters
          selectedFilter={selectedFilter}
          onFilterChange={handleFilterChange}
          searchTerm={searchTerm}
          selectedStatus={selectedStatus}
          onSearchChange={handleSearchChange}
          onSearchKeyPress={handleSearchKeyPress}
          onStatusChange={handleStatusChange}
        />
        <EventsSkeleton pageLimit={PaginationDefaults.EVENTS_LIMIT} />
      </Box>
    );
  }

  // Render empty state
  if (!isLoading && events.length === 0) {
    return (
      <Box sx={{ py: 1.5, px: 3 }}>
        <EventFilters
          selectedFilter={selectedFilter}
          onFilterChange={handleFilterChange}
          searchTerm={searchTerm}
          selectedStatus={selectedStatus}
          onSearchChange={handleSearchChange}
          onSearchKeyPress={handleSearchKeyPress}
          onStatusChange={handleStatusChange}
        />
        <EventsEmptyState />
      </Box>
    );
  }

  // Render split-pane layout
  return (
    <Stack sx={{ height: '86vh', display: 'flex', flexDirection: 'row' }}>
      {/* Left Panel: Events List */}
      <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: 2 }}>
        <EventFilters
          selectedFilter={selectedFilter}
          onFilterChange={handleFilterChange}
          selectedStatus={selectedStatus}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onSearchKeyPress={handleSearchKeyPress}
          onStatusChange={handleStatusChange}
        />
        <EventsTable events={events} selectedEventId={eventId} />
        {/* Load More */}
        {hasMore && (
          <Box sx={{ px: 3, py: 2 }}>
            <LoadMoreButton onClick={loadMore} isLoading={isLoadingMore} label="Load More Events" />
          </Box>
        )}
      </Box>

      <Divider sx={{ px: 1 }} orientation="vertical" flexItem />

      {/* Right Panel: Event Details */}
      {eventId && (
        <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: 2 }}>
          <EventDetailPane eventId={eventId} />
        </Box>
      )}
    </Stack>
  );
};

export default EventsListPage;
